/**
 * Одна заметка в списке: превью, которое ничего не прячет.
 *
 * РАСКРЫТИЕ УМЕРЛО (notes-v4, слова владельца: «пиздец как неудобно и непонятно, что для
 * появления панели с кнопками надо кликнуть на заголовок, а потом ещё на нужную кнопку»).
 * Карточка всегда выглядит одинаково и всегда показывает всё, что о заметке можно узнать НЕ
 * ОТКРЫВАЯ: дату, текст, кадры, место. Действий на ней ровно три, и все три видны сразу —
 * открыть (вся карточка), править (карандаш), удалить (корзина).
 *
 * ПОРЯДОК СВЕРХУ ВНИЗ НЕ ЗАВИСИТ НИ ОТ ОДНОГО СОСТОЯНИЯ: дата и органы → текст → лента →
 * место → очередь. Двадцать карточек читаются одним вертикальным движением глаза ровно
 * потому, что одноимённые вещи стоят на одной высоте от верха каждой.
 *
 * ВЛОЖЕННЫХ КНОПОК НЕ БЫВАЕТ, А КАРТОЧКА НАЖИМАЕТСЯ ЦЕЛИКОМ. Решение — покрытие: `::after`
 * кнопки-открывашки растянут на всю карточку, а органы, которым нажатие принадлежит самим
 * (карандаш, корзина, ячейки ленты, чип, строки очереди), стоят НАД ним по `z-index`. Так у
 * карточки одно доступное имя на всё полотно и при этом ни одной вложенной кнопки.
 */

import { useEffect, useRef, useState } from "react";

import type { FieldMarker, FieldPhoto } from "../api/types";
import { formatFull } from "../lib/format";
import { hasPlace, stripCells } from "../lib/notes";
import { isFileDrag } from "../map/drop";
import { Glyph, glyph } from "./DrawTools";
import { T } from "../strings";
import type { PhotoUpload } from "../state/useWorkspace";

/** Сколько стоит вопрос об удалении, если на него не ответили. Как в карточке поля. */
const DELETE_DISARM_MS = 5000;

export interface NoteCardProps {
  note: FieldMarker;
  /** Фотографии ЭТОЙ заметки, уже в порядке показа. */
  photos: FieldPhoto[];
  /** Место этой заметки указывают прямо сейчас: карточка обязана сказать, чего ждёт. */
  placing: boolean;
  /** Короткая вспышка после удачной постановки места — вторая половина ответа карты. */
  flashing: boolean;
  /** Строки очереди, чьи файлы едут в эту заметку. */
  uploads: PhotoUpload[];
  onRetryUpload: (uploadId: number) => void;
  /** Потолок фотографий НА ЗАМЕТКУ, как его назвал сервер. */
  noteLimit: number;
  /** Готовые миниатюры по id фотографии. */
  thumbs: Map<number, string>;
  /** Ref-обработчик корня: по нему хук миниатюр и узнаёт, что карточка на экране. */
  watch: (node: HTMLElement | null) => void;
  /** Открыть модалку: с первого кадра (`null`) или с названного. */
  onOpen: (photoId: number | null) => void;
  /** Открыть модалку сразу в правке — это карандаш. */
  onEdit: () => void;
  onDelete: () => Promise<{ ok: boolean; message: string | null }>;
  onAddPhotos: (files: File[]) => void;
  /** Взвести place-режим: чип у заметки без места. */
  onPlace: () => void;
  /** Долететь и мигнуть: чип у заметки с местом. Модалку НЕ открывает. */
  onShowOnMap: () => void;
}

export function NoteCard({
  note,
  photos,
  placing,
  flashing,
  uploads,
  onRetryUpload,
  noteLimit,
  thumbs,
  watch,
  onOpen,
  onEdit,
  onDelete,
  onAddPhotos,
  onPlace,
  onShowOnMap,
}: NoteCardProps) {
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [over, setOver] = useState(false);
  /** Отказ дропа: он обязан быть виден, а не съедать файлы молча. */
  const [refused, setRefused] = useState<string | null>(null);
  const yes = useRef<HTMLButtonElement | null>(null);

  /** Вопрос, на который не ответили, снимается сам — как в карточке поля. */
  useEffect(() => {
    if (!armed) return;
    const timer = window.setTimeout(() => setArmed(false), DELETE_DISARM_MS);
    return () => window.clearTimeout(timer);
  }, [armed]);

  /**
   * Кнопка, которой не было секунду назад, обязана получить каретку: иначе Tab ведёт мимо
   * вопроса, и человек, взведший его с клавиатуры, отвечать на него не может.
   */
  useEffect(() => {
    if (armed) yes.current?.focus();
  }, [armed]);

  /**
   * Карточка, у которой указывают место, подводится в поле зрения: режим включают из неё, но
   * карта под ним во весь экран, и уехавшая за край карточка оставляет человека перед
   * списком, в котором ничего не изменилось. `block: "nearest"` — не «наверх»: если она уже
   * видна, двигать список незачем.
   */
  const root = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!placing) return;
    const node = root.current;
    // В jsdom метода нет, и подводка не должна зависеть от того, даёт ли его среда.
    if (node && typeof node.scrollIntoView === "function")
      node.scrollIntoView({ block: "nearest" });
  }, [placing]);

  const placed = hasPlace(note);
  const photosFull = photos.length >= noteLimit;
  const text = note.note.trim();
  const date = formatFull(note.happened_on);

  const remove = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError(null);
    const result = await onDelete();
    setBusy(false);
    if (result.ok) return; // карточка уходит вместе с заметкой; рисовать больше нечего
    // Вопрос остаётся взведённым: прочитав причину, отвечают на него же.
    setError(result.message ?? T.common.unknownError);
  };

  // Закон ленты общий с модалкой (`lib/notes.ts`): четыре ячейки, пятая фотография превращает
  // последнюю в «+N», и «+N» открывает окно С ЧЕТВЁРТОЙ — три уже видны.
  const { shown, rest } = stripCells(photos);

  return (
    <article
      className={`k-notecard${placing ? " k-notecard--placing" : ""}${
        flashing ? " k-notecard--found" : ""
      }`}
      data-testid="note-card"
      data-note-id={note.id}
      data-over={over || undefined}
      ref={(node) => {
        root.current = node;
        watch(node);
      }}
      /* ДРОП НА КАРТОЧКУ — ЭТО «В ЭТУ ЗАМЕТКУ», И СОБЫТИЕ ГАСИТСЯ ЗДЕСЬ. Панель под карточкой
         тоже принимает файлы, но её ветка отдаёт их серверу без `marker_id` — по координатам,
         — и всплывший дроп заводил НОВУЮ заметку вместо той, на которую целились. */
      onDragOver={(event) => {
        if (!isFileDrag(event.dataTransfer)) return;
        event.preventDefault();
        event.stopPropagation();
        event.dataTransfer.dropEffect = photosFull ? "none" : "copy";
        if (!photosFull) setOver(true);
      }}
      onDragLeave={(event) => {
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
        setOver(false);
      }}
      onDrop={(event) => {
        if (!isFileDrag(event.dataTransfer)) return;
        event.preventDefault();
        event.stopPropagation();
        setOver(false);
        // Полная заметка файлов не принимает — и НЕ МОЛЧИТ об этом: отказ, которого не видно,
        // это не отказ, а пропажа.
        if (photosFull) {
          setRefused(T.photos.limitNote(noteLimit));
          return;
        }
        setRefused(null);
        const files = [...event.dataTransfer.files];
        if (files.length > 0) onAddPhotos(files);
      }}
    >
      <div className="k-notecard__top">
        {/* ОТКРЫВАШКА. Её `::after` накрывает карточку целиком (FieldNotes.css), поэтому
            доступное имя у неё — имя всей заметки, то же самое, что у маркера на карте: одна
            заметка звучит одинаково на обеих поверхностях. */}
        <button
          type="button"
          className="k-notecard__open"
          data-testid="note-open"
          aria-label={T.markers.aria(date, text || null, photos.length)}
          onClick={() => onOpen(null)}
        >
          <span className="k-mono k-notecard__date">{date}</span>
        </button>

        {/* ДВА ОРГАНА С ВИДИМОЙ РАМКОЙ, а не голые глифы. Прецедент поимённый: `k-iconbtn--slim`
            был удалён из `base.css` ровно потому, что в покое выглядел безымянным глифом с
            прозрачной рамкой — «The affordance was invisible». Повторять это в двадцати
            карточках подряд нельзя. И не `.k-iconbtn`: тот написан для кнопки НА СНИМКЕ, с
            поднятой тенью, а двадцать поднятых теней в колонке — то, от чего избавлялся
            ростер. */}
        <div className="k-notecard__tools">
          <button
            type="button"
            className="k-notecard__tool"
            data-testid="note-edit"
            aria-label={T.markers.editAria(date)}
            onClick={onEdit}
          >
            <Glyph d={glyph.edit} size={16} stroke={1.75} />
          </button>
          <button
            type="button"
            className="k-notecard__tool k-notecard__tool--danger"
            data-testid="note-delete"
            aria-label={T.markers.deleteAria(date)}
            aria-pressed={armed}
            onClick={() => setArmed(true)}
          >
            <Glyph d={glyph.remove} size={16} stroke={1.75} />
          </button>
        </div>
      </div>

      {error !== null && (
        <p className="k-note k-note--error" role="alert" data-testid="note-error">
          {error}
        </p>
      )}

      {/* ВОПРОС ВСТАЁТ НА МЕСТЕ ТЕКСТА, А ЛЕНТА ОСТАЁТСЯ ВИДНОЙ: человек обязан видеть, что
          именно он теряет. Число фотографий в вопросе обязательно — удаление заметки уносит
          их с собой, и это главное, что он хочет знать. */}
      {armed ? (
        <div className="k-note k-note--error" role="alert" data-testid="note-delete-ask">
          <p>{T.markers.deleteAsk(photos.length)}</p>
          <div className="k-row k-row--wrap">
            <button
              type="button"
              className="k-btn k-btn--danger k-btn--compact"
              ref={yes}
              disabled={busy}
              onClick={() => void remove()}
            >
              {T.tools.deleteYes}
            </button>
            <button
              type="button"
              className="k-btn k-btn--ghost k-btn--compact"
              onClick={() => setArmed(false)}
            >
              {T.tools.deleteNo}
            </button>
          </div>
        </div>
      ) : text !== "" ? (
        // Клэмп 2 строки: карточка — превью, а не чтение. Полный текст — в модалке.
        <p className="k-notecard__text">{text}</p>
      ) : photos.length === 0 ? (
        // Ни слов, ни кадров — назвать заметку больше нечем. При живой ленте эта строка не
        // печатается: содержимое у заметки есть, и оно видно ячейкой прямо под заголовком.
        <p className="k-notecard__text k-notecard__text--none">{T.markers.noText}</p>
      ) : null}

      {photos.length > 0 && (
        <div className="k-notecard__strip" data-testid="note-strip">
          {shown.map((photo) => {
            const url = thumbs.get(photo.id);
            return (
              <button
                key={photo.id}
                type="button"
                className="k-notecard__cell"
                aria-label={formatFull(photo.taken_on)}
                onClick={() => onOpen(photo.id)}
              >
                {url !== undefined && (
                  <img
                    src={url}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    width={photo.width}
                    height={photo.height}
                  />
                )}
              </button>
            );
          })}
          {rest > 0 && (
            <button
              type="button"
              className="k-notecard__more"
              data-testid="note-more"
              onClick={() => onOpen(photos[shown.length]?.id ?? null)}
            >
              +{rest}
            </button>
          )}
        </div>
      )}

      {/* ЧИП МЕСТА — состояние и орган сразу, три вида. Стоит НАД покрытием: «где эта заметка»
          и «покажи мне эту заметку» — разные вопросы, и второй не должен по дороге открывать
          модалку. Третий вид — не кнопка: пока ждут клика по карте, нажимать здесь нечего. */}
      {placing ? (
        <p className="k-hint k-notecard__chip" data-testid="note-place">
          {T.markers.placingCard}
        </p>
      ) : (
        <button
          type="button"
          /* Заметка С МЕСТОМ помечена тоном: «На карте» и «Указать место» различались только
             словами, то есть чтением, — а по колонке из двадцати карточек состояние обязано
             читаться одним взглядом. */
          className={`k-notecard__chip k-notecard__chip--btn${
            placed ? " k-notecard__chip--placed" : ""
          }`}
          data-testid="note-place"
          onClick={placed ? onShowOnMap : onPlace}
        >
          <Glyph d={glyph.flag} size={16} stroke={1.5} />
          {placed ? T.markers.onMap : T.markers.noPlace}
        </button>
      )}

      {refused !== null && (
        <div className="k-note k-note--error" role="alert" data-testid="note-refused">
          <p>{refused}</p>
          <button
            type="button"
            className="k-btn k-btn--ghost k-btn--compact"
            onClick={() => setRefused(null)}
          >
            {T.common.close}
          </button>
        </div>
      )}

      {uploads.length > 0 && (
        <div className="k-queue" data-testid="note-queue">
          {uploads.map((row) => (
            <div className="k-queuerow" key={row.id}>
              <span className="k-notecard__filename" title={row.file.name}>
                {row.file.name}
              </span>
              {row.status === "error" ? (
                <button
                  type="button"
                  className="k-btn k-btn--compact"
                  onClick={() => onRetryUpload(row.id)}
                >
                  {T.common.retry}
                </button>
              ) : (
                <span className="k-queuerow__status">
                  {row.status === "done" ? T.photos.queueDone : T.common.loading}
                </span>
              )}
              {row.error !== null && (
                <p className="k-note k-note--error k-notecard__rowerror" role="alert">
                  {row.error}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </article>
  );
}
